import { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../ui/alert-dialog';
import {
  Plus, Users, Search, Trash2, Edit, Loader2,
  Phone, Mail, Building2, Briefcase,
} from 'lucide-react';
import { ExportDropdown } from '../shared/PageActions';
import ImportDialog from '../shared/ImportDialog';
import { getDefaultCompanyInfo } from '../../lib/brandConfig';
import { previewContactPdf, downloadContactPdf } from '../../lib/infoCardPdfExport';
import { API } from '../../lib/api';
import axios from 'axios';
import { Upload } from 'lucide-react';

// ── Category badge ──────────────────────────────────────────────────────────
function CategoryBadge({ category }) {
  const styles = {
    client:        { bg: '#dbeafe', color: '#1e40af' },
    supplier:      { bg: '#fce7f3', color: '#9d174d' },
    subcontractor: { bg: '#fef3c7', color: '#92400e' },
    consultant:    { bg: '#ede9fe', color: '#5b21b6' },
    internal:      { bg: '#d1fae5', color: '#065f46' },
  };
  const s = styles[category] || styles.client;
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-normal whitespace-nowrap"
      style={{ background: s.bg, color: s.color }}
    >
      {category}
    </span>
  );
}

// ── KPI bar ─────────────────────────────────────────────────────────────────
function KPIBar({ contacts }) {
  const clients = contacts.filter(c => c.category === 'client').length;
  const suppliers = contacts.filter(c => c.category === 'supplier').length;
  const subs = contacts.filter(c => c.category === 'subcontractor').length;
  const consultants = contacts.filter(c => c.category === 'consultant').length;

  return (
    <div className="flex flex-wrap gap-3 mb-4">
      {[
        { label: 'Clients', value: clients, color: '#3b82f6' },
        { label: 'Suppliers', value: suppliers, color: '#ec4899' },
        { label: 'Subcontractors', value: subs, color: '#f59e0b' },
        { label: 'Consultants', value: consultants, color: '#8b5cf6' },
      ].map(kpi => (
        <div
          key={kpi.label}
          className="flex items-center gap-2 px-3 py-2 rounded-md"
          style={{ background: '#f8f7f4', border: '1px solid #e5e4df' }}
        >
          <span className="text-2xl font-bold" style={{ fontFamily: "'JetBrains Mono', monospace", color: kpi.color }}>
            {kpi.value}
          </span>
          <span className="text-xs text-[#6b7280]">{kpi.label}</span>
        </div>
      ))}
    </div>
  );
}

// ── New contact modal ───────────────────────────────────────────────────────
function NewContactModal({ open, onClose, onCreated, clients }) {
  const [form, setForm] = useState({
    full_name: '', company: '', client_id: '',
    category: 'client', title: '',
    email: '', phone: '', mobile: '', website: '',
    specialties: '', notes: '',
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!form.full_name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        specialties: form.specialties
          ? form.specialties.split(',').map(s => s.trim()).filter(Boolean)
          : [],
      };
      const res = await axios.post(`${API}/contacts`, payload);
      onCreated(res.data);
      setForm({
        full_name: '', company: '', client_id: '',
        category: 'client', title: '',
        email: '', phone: '', mobile: '', website: '',
        specialties: '', notes: '',
      });
      onClose();
    } catch (err) {
      console.error('Failed to create contact:', err);
    }
    setSaving(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b" style={{ borderColor: '#e5e4df' }}>
          <h3 className="text-lg font-semibold text-[#1a1a2e]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            New Contact
          </h3>
        </div>
        <div className="px-6 py-4 space-y-3">
          <Field label="Full Name *" value={form.full_name} onChange={v => handleChange('full_name', v)} />
          <Field label="Title" value={form.title} onChange={v => handleChange('title', v)} placeholder="e.g. Project Manager" />
          <Field label="Company" value={form.company} onChange={v => handleChange('company', v)} />
          <div>
            <label className="block text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-1">Linked Client</label>
            <select
              value={form.client_id}
              onChange={e => handleChange('client_id', e.target.value)}
              className="w-full px-3 py-2 border rounded text-sm"
              style={{ borderColor: '#e5e4df' }}
            >
              <option value="">— None —</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.company_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-1">Category</label>
            <select
              value={form.category}
              onChange={e => handleChange('category', e.target.value)}
              className="w-full px-3 py-2 border rounded text-sm"
              style={{ borderColor: '#e5e4df' }}
            >
              <option value="client">Client</option>
              <option value="supplier">Supplier</option>
              <option value="subcontractor">Subcontractor</option>
              <option value="consultant">Consultant</option>
              <option value="internal">Internal</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email" value={form.email} onChange={v => handleChange('email', v)} type="email" />
            <Field label="Phone" value={form.phone} onChange={v => handleChange('phone', v)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Mobile" value={form.mobile} onChange={v => handleChange('mobile', v)} />
            <Field label="Website" value={form.website} onChange={v => handleChange('website', v)} />
          </div>
          <Field label="Specialties" value={form.specialties} onChange={v => handleChange('specialties', v)} placeholder="Comma-separated, e.g. Plumbing, Electrical" />
          <Field label="Notes" value={form.notes} onChange={v => handleChange('notes', v)} multiline />
        </div>
        <div className="px-6 py-4 border-t flex justify-end gap-2" style={{ borderColor: '#e5e4df' }}>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={saving || !form.full_name.trim()}
            className="bg-[#c9a84c] text-[#1a1a2e] hover:bg-[#b8973e] font-semibold"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Create Contact
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, multiline, placeholder }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-[#6b7280] uppercase tracking-wider mb-1">{label}</label>
      {multiline ? (
        <textarea
          value={value}
          onChange={e => onChange(e.target.value)}
          rows={3}
          placeholder={placeholder}
          className="w-full px-3 py-2 border rounded text-sm resize-none"
          style={{ borderColor: '#e5e4df' }}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full px-3 py-2 border rounded text-sm"
          style={{ borderColor: '#e5e4df' }}
        />
      )}
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────────
export default function ContactsTab({ onEditContact }) {
  const [contacts, setContacts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  // ── Fetch contacts ─────────────────────────────────────────────────────
  const fetchContacts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterCategory) params.set('category', filterCategory);
      if (searchQuery) params.set('search', searchQuery);
      const res = await axios.get(`${API}/contacts?${params.toString()}`);
      setContacts(res.data.data || []);
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
    }
  }, [filterCategory, searchQuery]);

  // ── Fetch clients (for modal dropdown) ─────────────────────────────────
  useEffect(() => {
    axios.get(`${API}/clients`).then(res => setClients(res.data.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    fetchContacts().finally(() => setLoading(false));
  }, [fetchContacts]);

  // ── Delete ─────────────────────────────────────────────────────────────
  const handleDeleteContact = async (id) => {
    try {
      await axios.delete(`${API}/contacts/${id}`);
      setContacts(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      console.error('Failed to delete contact:', err);
    }
  };

  // ── Search debounce ────────────────────────────────────────────────────
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // ── PDF export ─────────────────────────────────────────────────────────
  const companyInfo = getDefaultCompanyInfo();
  const handlePdfPreview = () => {
    if (contacts.length > 0) previewContactPdf(contacts[0], companyInfo);
  };
  const handlePdfDownload = () => {
    if (contacts.length > 0) downloadContactPdf(contacts[0], companyInfo);
  };
  const handlePrint = () => window.print();

  // ── Get client name by id ──────────────────────────────────────────────
  const getClientName = (clientId) => {
    if (!clientId) return null;
    const client = clients.find(c => c.id === clientId);
    return client?.company_name || null;
  };

  // ── Render ─────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="text-center py-16 text-[#6b7280]">
        <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin opacity-40" />
        Loading...
      </div>
    );
  }

  return (
    <>
      {/* KPI */}
      <KPIBar contacts={contacts} />

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 flex-1 w-full sm:w-auto">
          {/* Search */}
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9ca3af]" />
            <input
              type="text"
              placeholder="Search contacts..."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-sm border rounded"
              style={{ borderColor: '#e5e4df' }}
            />
          </div>
          {/* Category filter */}
          <select
            value={filterCategory}
            onChange={e => setFilterCategory(e.target.value)}
            className="px-2 py-1.5 text-sm border rounded"
            style={{ borderColor: '#e5e4df' }}
          >
            <option value="">All Categories</option>
            <option value="client">Client</option>
            <option value="supplier">Supplier</option>
            <option value="subcontractor">Subcontractor</option>
            <option value="consultant">Consultant</option>
            <option value="internal">Internal</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <ExportDropdown onPdfPreview={handlePdfPreview} onPdfDownload={handlePdfDownload} onPrint={handlePrint} />
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowImportModal(true)}
            title="Import contacts from CSV/Excel"
          >
            <Upload className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Import</span>
          </Button>
          <Button
            onClick={() => setShowNewModal(true)}
            size="sm"
            className="bg-[#c9a84c] text-[#1a1a2e] hover:bg-[#b8973e] font-semibold"
          >
            <Plus className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">New Contact</span>
          </Button>
        </div>
      </div>

      {/* List */}
      {contacts.length === 0 ? (
        <div className="text-center py-16">
          <Users className="w-16 h-16 mx-auto text-[#c9a84c]/40 mb-4" />
          <p className="text-[#6b7280] text-lg mb-2">No contacts yet</p>
          <p className="text-[#6b7280]/60 text-sm mb-6">Create your first contact to get started</p>
          <Button onClick={() => setShowNewModal(true)} className="bg-[#1a1a2e] text-white hover:bg-[#252545]">
            <Plus className="w-4 h-4 mr-2" />
            Create Contact
          </Button>
        </div>
      ) : (
        <div className="space-y-1">
          {/* Desktop header */}
          <div
            className="hidden lg:grid gap-3 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-[#6b7280]"
            style={{ gridTemplateColumns: '1fr 120px 120px 160px 60px 60px 100px' }}
          >
            <span>Name</span>
            <span>Company</span>
            <span>Category</span>
            <span>Contact</span>
            <span className="text-center"><Phone className="w-3.5 h-3.5 inline" /></span>
            <span className="text-center"><Mail className="w-3.5 h-3.5 inline" /></span>
            <span className="text-right">Actions</span>
          </div>

          {contacts.map(c => {
            const linkedClient = getClientName(c.client_id);
            return (
              <div key={c.id}>
                {/* Desktop row */}
                <div
                  className="hidden lg:grid gap-3 px-4 py-3 rounded-md hover:bg-[#f3f2ef] transition-colors cursor-pointer items-center"
                  style={{
                    gridTemplateColumns: '1fr 120px 120px 160px 60px 60px 100px',
                    borderBottom: '1px solid rgba(201,168,76,0.1)',
                  }}
                  onClick={() => onEditContact(c.id)}
                >
                  <div>
                    <span className="text-sm font-semibold text-[#1a1a2e]">{c.full_name}</span>
                    {c.title && <span className="text-xs text-[#6b7280] ml-2">{c.title}</span>}
                  </div>
                  <span className="text-xs text-[#6b7280] truncate">{c.company || linkedClient || '—'}</span>
                  <CategoryBadge category={c.category} />
                  <div className="flex items-center gap-2">
                    {c.email && <span className="text-xs text-[#6b7280] truncate">{c.email}</span>}
                    {!c.email && c.phone && <span className="text-xs text-[#6b7280] truncate">{c.phone}</span>}
                  </div>
                  <span className="text-center">
                    {c.phone || c.mobile ? (
                      <a href={`tel:${c.phone || c.mobile}`} onClick={e => e.stopPropagation()} className="text-[#10b981] hover:text-[#065f46]"><Phone className="w-4 h-4 inline" /></a>
                    ) : <span className="text-[#d1d5db]">—</span>}
                  </span>
                  <span className="text-center">
                    {c.email ? (
                      <a href={`mailto:${c.email}`} onClick={e => e.stopPropagation()} className="text-[#3b82f6] hover:text-[#1e40af]"><Mail className="w-4 h-4 inline" /></a>
                    ) : <span className="text-[#d1d5db]">—</span>}
                  </span>
                  <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                    <button onClick={() => onEditContact(c.id)} className="p-1.5 rounded hover:bg-[#c9a84c]/10 text-[#6b7280] hover:text-[#1a1a2e] transition-colors" title="Edit"><Edit className="w-4 h-4" /></button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild><button className="p-1.5 rounded hover:bg-red-50 text-[#6b7280] hover:text-[#dc2626] transition-colors"><Trash2 className="w-4 h-4" /></button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Delete Contact</AlertDialogTitle><AlertDialogDescription>Are you sure you want to delete {c.full_name}? This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => handleDeleteContact(c.id)} className="bg-[#dc2626] hover:bg-[#b91c1c]">Delete</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>

                {/* Mobile card */}
                <div
                  className="lg:hidden px-3 py-3 cursor-pointer hover:bg-[#f3f2ef] transition-colors rounded-md"
                  style={{ borderBottom: '1px solid rgba(201,168,76,0.12)' }}
                  onClick={() => onEditContact(c.id)}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="text-sm font-semibold text-[#1a1a2e]">{c.full_name}</span>
                      {c.title && <span className="text-xs text-[#6b7280] ml-1">{c.title}</span>}
                    </div>
                    <CategoryBadge category={c.category} />
                  </div>
                  {(c.company || linkedClient) && (
                    <div className="flex items-center gap-1 mt-1 text-xs text-[#6b7280]">
                      <Building2 className="w-3 h-3" />
                      {c.company || linkedClient}
                    </div>
                  )}
                  <div className="flex items-center gap-3 mt-2 text-xs text-[#6b7280]">
                    {c.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>}
                    {(c.phone || c.mobile) && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone || c.mobile}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* New contact modal */}
      <NewContactModal
        open={showNewModal}
        onClose={() => setShowNewModal(false)}
        onCreated={(newContact) => {
          setContacts(prev => [newContact, ...prev]);
        }}
        clients={clients}
      />

      {/* Import modal */}
      <ImportDialog
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Import Contacts"
        type="contacts"
        onImported={(res) => {
          if (res.success > 0) fetchContacts();
        }}
      />
    </>
  );
}
